'use client';

/**
 * Primitives d'animation au défilement pour le site vitrine.
 * Fondées sur Framer Motion + `whileInView` (déclenché une seule fois).
 * `prefers-reduced-motion` est respecté : l'élément apparaît alors sans transform.
 */
import React from 'react';
import { motion, useReducedMotion, type Variants } from 'framer-motion';

const EASE = [0.22, 1, 0.36, 1] as const;

type Direction = 'up' | 'down' | 'left' | 'right' | 'none';

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  /** Délai avant l'apparition (s). */
  delay?: number;
  /** Durée de l'apparition (s). */
  duration?: number;
  /** Sens d'entrée. */
  from?: Direction;
  /** Amplitude du déplacement (px). */
  distance?: number;
  /** Rejoue à chaque entrée dans le viewport (par défaut : une seule fois). */
  repeat?: boolean;
}

function offset(from: Direction, distance: number): { x: number; y: number } {
  switch (from) {
    case 'up':
      return { x: 0, y: distance };
    case 'down':
      return { x: 0, y: -distance };
    case 'left':
      return { x: distance, y: 0 };
    case 'right':
      return { x: -distance, y: 0 };
    default:
      return { x: 0, y: 0 };
  }
}

export function Reveal({
  children,
  className,
  delay = 0,
  duration = 0.7,
  from = 'up',
  distance = 26,
  repeat = false,
}: RevealProps) {
  const reduce = useReducedMotion();
  const { x, y } = reduce ? { x: 0, y: 0 } : offset(from, distance);

  return (
    <motion.div data-component-id="src/components/marketing/motion/Reveal.tsx:58:4" data-component-path="src/components/marketing/motion/Reveal.tsx" data-component-line="58" data-component-end-line="66" data-component-file="Reveal.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D"
      className={className}
      initial={{ opacity: 0, x, y }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: !repeat, margin: '-90px' }}
      transition={{ duration: reduce ? 0.001 : duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/** Conteneur qui décale l'apparition de ses enfants <StaggerItem>. */
export function Stagger({
  children,
  className,
  delayChildren = 0.05,
  staggerChildren = 0.09,
}: {
  children: React.ReactNode;
  className?: string;
  delayChildren?: number;
  staggerChildren?: number;
}) {
  const variants: Variants = {
    hidden: {},
    show: {
      transition: { delayChildren, staggerChildren },
    },
  };

  return (
    <motion.div data-component-id="src/components/marketing/motion/Reveal.tsx:90:4" data-component-path="src/components/marketing/motion/Reveal.tsx" data-component-line="90" data-component-end-line="98" data-component-file="Reveal.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D"
      className={className}
      variants={variants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-80px' }}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({
  children,
  className,
  from = 'up',
  distance = 22,
}: {
  children: React.ReactNode;
  className?: string;
  from?: Direction;
  distance?: number;
}) {
  const reduce = useReducedMotion();
  const { x, y } = reduce ? { x: 0, y: 0 } : offset(from, distance);

  const variants: Variants = {
    hidden: { opacity: 0, x, y },
    show: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: { duration: reduce ? 0.001 : 0.65, ease: EASE },
    },
  };

  return (
    <motion.div data-component-id="src/components/marketing/motion/Reveal.tsx:127:4" data-component-path="src/components/marketing/motion/Reveal.tsx" data-component-line="127" data-component-end-line="129" data-component-file="Reveal.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className} variants={variants}>
      {children}
    </motion.div>
  );
}
